import {
  Autocomplete,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Grid,
  Switch,
  TextField,
} from "@mui/material";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import {
  getAllServices,
  inviteClinician,
} from "../../../api/services/api";

interface InviteClinicianDialogProps {
  open: boolean;
  onClose: () => void;
}

const emptyForm = {
  fullName: "",
  email: "",
  mobileNumber: "",
  designation: "",
  sendInvite: true,
};

function InviteClinicianDialog({ open, onClose }: InviteClinicianDialogProps) {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({ ...emptyForm });
  const [selectedServices, setSelectedServices] = useState<any[]>([]);

  const { data: serviceData } = useQuery("services", getAllServices, {
    enabled: open,
  });

  const services = serviceData?.data?.services || [];

  const reset = () => {
    setFormData({ ...emptyForm });
    setSelectedServices([]);
  };

  const inviteMutation = useMutation((data: any) => inviteClinician(data), {
    onSuccess: (res: any) => {
      const invited = res?.data?.inviteSent ?? res?.inviteSent;
      toast.success(
        invited
          ? "Clinician invited. Onboarding email sent."
          : "Clinician created. Invite email was not sent."
      );
      queryClient.invalidateQueries(["therapists"]);
      reset();
      onClose();
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.message ||
          "Failed to invite clinician. Please try again."
      );
    },
  });

  const handleChange = (field: string) => (event: any) => {
    setFormData({ ...formData, [field]: event.target.value });
  };

  const handleSubmit = () => {
    if (!formData.fullName.trim()) {
      toast.error("Full name is required");
      return;
    }
    if (!formData.email.trim()) {
      toast.error("Email is required");
      return;
    }

    const body: any = {
      fullName: formData.fullName.trim(),
      email: formData.email.trim(),
      sendInvite: formData.sendInvite,
    };
    if (formData.mobileNumber.trim()) {
      body.mobileNumber = formData.mobileNumber.trim();
    }
    if (formData.designation.trim()) {
      body.designation = formData.designation.trim();
    }
    if (selectedServices.length) {
      body.serviceIds = selectedServices.map((s) => s.id);
    }

    inviteMutation.mutate(body);
  };

  const handleClose = () => {
    if (inviteMutation.isLoading) return;
    reset();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Invite Clinician</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 1 }}>
          Create a clinician account without a password. They will receive an
          email with a secure link to complete onboarding and set their own
          password.
        </DialogContentText>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="Full Name"
              value={formData.fullName}
              onChange={handleChange("fullName")}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="Email"
              type="email"
              value={formData.email}
              onChange={handleChange("email")}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Mobile Number (optional)"
              placeholder="+9665XXXXXXXX"
              value={formData.mobileNumber}
              onChange={handleChange("mobileNumber")}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Designation (optional)"
              placeholder="Speech Therapist"
              value={formData.designation}
              onChange={handleChange("designation")}
            />
          </Grid>
          <Grid item xs={12}>
            <Autocomplete
              multiple
              options={Array.isArray(services) ? services : []}
              getOptionLabel={(option: any) =>
                option?.title || option?.name || ""
              }
              value={selectedServices}
              onChange={(_, value) => setSelectedServices(value)}
              disableCloseOnSelect
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Checkbox style={{ marginRight: 8 }} checked={selected} />
                  {option?.title || option?.name}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Services (optional)"
                  placeholder="Select services"
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.sendInvite}
                  onChange={(e) =>
                    setFormData({ ...formData, sendInvite: e.target.checked })
                  }
                />
              }
              label="Send invite email"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={inviteMutation.isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={inviteMutation.isLoading}
        >
          {inviteMutation.isLoading ? "Sending..." : "Send Invite"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default InviteClinicianDialog;
